import { useMemo, useState } from 'react';
import { Check, ChevronDown, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';

export type SearchableSelectOption = {
  id: string;
  label: string;
  searchText?: string;
};

export function SearchableSelectWithAdd({
  label,
  value,
  onValueChange,
  options,
  placeholder = 'Select…',
  searchPlaceholder = 'Search…',
  emptyMessage = 'No matches found',
  disabled = false,
  addDisabled = false,
  onAdd,
  addTitle = 'Add new',
  className,
}: {
  label: string;
  value: string;
  onValueChange: (value: string) => void;
  options: SearchableSelectOption[];
  placeholder?: string;
  searchPlaceholder?: string;
  emptyMessage?: string;
  disabled?: boolean;
  addDisabled?: boolean;
  onAdd?: () => void;
  addTitle?: string;
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  const selected = options.find((o) => o.id === value);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) => {
      const haystack = `${o.label} ${o.searchText ?? ''}`.toLowerCase();
      return haystack.includes(q);
    });
  }, [options, search]);

  return (
    <div className={className}>
      <Label>{label}</Label>
      <div className="mt-1.5 flex gap-2">
        <DropdownMenu
          open={open}
          onOpenChange={(next) => {
            setOpen(next);
            if (!next) setSearch('');
          }}
        >
          <DropdownMenuTrigger asChild>
            <Button
              type="button"
              variant="outline"
              disabled={disabled}
              className={cn(
                'h-9 flex-1 justify-between px-3 font-normal',
                !selected && 'text-muted-foreground',
              )}
            >
              <span className="truncate">{selected?.label ?? placeholder}</span>
              <ChevronDown className="h-4 w-4 shrink-0 opacity-50" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="start"
            className="w-[var(--radix-dropdown-menu-trigger-width)] p-0"
            onCloseAutoFocus={(e) => e.preventDefault()}
          >
            <div className="border-b p-2">
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={searchPlaceholder}
                className="h-8"
                onKeyDown={(e) => e.stopPropagation()}
                onClick={(e) => e.stopPropagation()}
              />
            </div>
            <div className="max-h-52 overflow-y-auto p-1">
              {filtered.length === 0 ? (
                <p className="px-2 py-3 text-center text-xs text-muted-foreground">{emptyMessage}</p>
              ) : (
                filtered.map((option) => (
                  <DropdownMenuItem
                    key={option.id}
                    className="flex items-center justify-between gap-2"
                    onSelect={() => {
                      onValueChange(option.id);
                      setOpen(false);
                      setSearch('');
                    }}
                  >
                    <span className="truncate">{option.label}</span>
                    {value === option.id && <Check className="h-4 w-4 shrink-0 text-gold-600" />}
                  </DropdownMenuItem>
                ))
              )}
            </div>
          </DropdownMenuContent>
        </DropdownMenu>

        {onAdd && (
          <Button
            type="button"
            variant="outline"
            size="icon"
            className="h-9 w-9 shrink-0"
            disabled={addDisabled}
            title={addTitle}
            onClick={onAdd}
          >
            <Plus className="h-4 w-4" />
          </Button>
        )}
      </div>
    </div>
  );
}
